import { ChangeEvent, FC, ReactElement } from 'react';
import { makeStyles, Tab, Tabs, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useWbTabsContext } from './WbTabsContext';
import { WbTooltip } from '../WbTooltip';
import { TabBase, TabValue } from './types';
import { getFocusVisibleStyle } from '../../styles';

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      width: '100%',
    },
    indicator: {
      display: 'none',
    },
    tabRoot: {
      width: '100%',
      maxWidth: '100%',
      textTransform: 'none',
      padding: theme.spacing(0.5, 1),
      height: 40,
      borderRadius: 0,
      borderRight: `1px solid transparent`,
      borderLeft: `1px solid transparent`,
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      color: theme.palette.text.primary,
      fontWeight: 500,
      opacity: 1,
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
      },
      '&.Mui-focusVisible': {
        ...getFocusVisibleStyle(theme),
      },
      '&.Mui-disabled': {
        opacity: 0.5,
      },
    },
    selectedTab: {
      backgroundColor: theme.palette.grey[100],
      border: `1px solid ${theme.palette.blueGrey[100]}`,
      color: theme.palette.text.primary,
      fontWeight: 500,
      borderRadius: 6,
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
    nextTabSelected: {
      borderBottom: 'none',
    },
    tab: {
      color: theme.palette.text.primary,
    },
    labelIcon: {
      minHeight: 'auto',
    },
    wrapperWithIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexDirection: 'row-reverse',
      '&>:first-child': {
        marginBottom: '0px !important',
      },
    },
    wrapperWithoutIcon: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'space-between',
    },
  }),
  { name: 'WbVerticalTabs' },
);

interface VerticalTabItem extends TabBase {
  label: ReactElement | string;
  disabled?: boolean;
  tooltip?: string;
  icon?: ReactElement;
}

interface WbVerticalTabsProps {
  tabs: VerticalTabItem[];
  'aria-label'?: string;
}

export const WbVerticalTabs: FC<WbVerticalTabsProps> = ({
  tabs,
  'aria-label': ariaLabel = 'Vertical tabs',
}) => {
  const classes = useStyles();
  const { value, onValueChange, getTabId, getPanelId } = useWbTabsContext();

  const handleChange = (_e: ChangeEvent<{}>, newValue: any) => {
    onValueChange?.(newValue);
  };

  const a11yProps = (val: TabValue) => {
    return {
      id: getTabId(val),
      'aria-controls': getPanelId(val),
    };
  };

  return (
    <Tabs
      selectionFollowsFocus
      orientation="vertical"
      variant="scrollable"
      value={value}
      onChange={handleChange}
      aria-label={ariaLabel}
      classes={{
        root: classes.root,
        indicator: classes.indicator,
      }}
    >
      {tabs.map((tab, index) =>
        tab.tooltip ? (
          <WbTooltip title={tab.tooltip} key={index}>
            <Tab
              label={tab.label}
              icon={tab.icon}
              value={tab.value}
              // needed to make MUI tab work with wrapped tooltip
              selected={value === tab.value}
              disabled={tab.disabled}
              classes={{
                root: clsx(classes.tabRoot, {
                  [classes.nextTabSelected]: value === tabs[index + 1]?.value,
                }),
                selected: classes.selectedTab,
                labelIcon: classes.labelIcon,
                wrapper: tab?.icon
                  ? classes.wrapperWithIcon
                  : classes.wrapperWithoutIcon,
              }}
              {...a11yProps(tab.value)}
            />
          </WbTooltip>
        ) : (
          <Tab
            key={index}
            label={tab.label}
            icon={tab.icon}
            value={tab.value}
            disabled={tab.disabled}
            classes={{
              root: clsx(classes.tabRoot, {
                [classes.nextTabSelected]: value === tabs[index + 1]?.value,
              }),
              selected: classes.selectedTab,
              labelIcon: classes.labelIcon,
              wrapper: tab?.icon
                ? classes.wrapperWithIcon
                : classes.wrapperWithoutIcon,
            }}
            {...a11yProps(tab.value)}
          />
        ),
      )}
    </Tabs>
  );
};
